import { baseApi } from "@/core/api/baseApi";
import type {
  Patient,
  CreatePatientPayload,
  UpdatePatientPayload,
  PatientsListResponse,
  PatientsQueryParams,
  Assessment,
  CreateCurrentAssessmentPayload,
} from "../../domain/entities/Patient";
import type {
  PatientDTO,
  PatientsListResponseDTO,
  PatientByIdResponseDTO,
  CreatePatientResponseDTO,
  AssessmentsListResponseDTO,
} from "../../domain/dto/PatientDTO";
import type {
  AccessibilitySensoryDTO,
  AccessibilitySensoryResponseDTO,
  AssessmentDetailDTO,
  CaregiverQuestionnaireDTO,
  CaregiverQuestionnaireResponseDTO,
  PatientAssessmentDetailDTO,
} from "../../domain/dto/ClinicalExaminationDTO";
import type {
  SensoryFormData,
  NeurologicalHistoryFormData,
  BehavioralFormData,
  CognitiveFunctionalFormData,
} from "../../presentation/pages/ClinicalExamination/types";
import {
  initialNeurologicalFormData,
  initialBehavioralFormData,
  initialCognitiveFunctionalFormData,
} from "../../presentation/pages/ClinicalExamination/types";
import {
  mapSensoryFormDataToAccessibilitySensoryDTO,
  mapCaregiverQuestionnaireRequestBody,
  mapSkippedSensoryStepToAccessibilitySensoryDTO,
} from "./mappers/clinicalExaminationMapper";
import {
  mapAssessmentsListResponseDTO,
  mapPatientDTO,
  mapPatientsListResponseDTO,
} from "./mappers/patientMapper";

export const patientsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // â”€â”€ PATIENT CRUD â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    getPatients: builder.query<PatientsListResponse, PatientsQueryParams>({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params.query) qs.append("query", params.query);
        if (params.page) qs.append("page", params.page.toString());
        if (params.page_size)
          qs.append("page_size", params.page_size.toString());
        if (params.sort_by) qs.append("sort_by", params.sort_by);
        if (params.sort_order) qs.append("sort_order", params.sort_order);
        const queryString = qs.toString();
        return {
          url: `/patients${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        };
      },
      transformResponse: (
        response: PatientsListResponseDTO,
      ): PatientsListResponse => mapPatientsListResponseDTO(response),
      providesTags: (result) =>
        result
          ? [
              ...result.patients.map(({ id }) => ({
                type: "Patients" as const,
                id,
              })),
              { type: "Patients", id: "LIST" },
            ]
          : [{ type: "Patients", id: "LIST" }],
    }),

    getPatientById: builder.query<Patient, string>({
      query: (patientId) => ({ url: `/patients/${patientId}`, method: "GET" }),
      transformResponse: (response: PatientByIdResponseDTO): Patient =>
        mapPatientDTO(response.data),
      providesTags: (result, error, patientId) => [
        { type: "Patients", id: patientId },
      ],
    }),

    createPatient: builder.mutation<Patient, CreatePatientPayload>({
      query: (body) => ({ url: "/patients", method: "POST", body }),
      transformResponse: (response: CreatePatientResponseDTO): Patient =>
        mapPatientDTO(response.data),
      invalidatesTags: [{ type: "Patients", id: "LIST" }],
    }),

    updatePatient: builder.mutation<
      Patient,
      { patientId: string; data: UpdatePatientPayload }
    >({
      query: ({ patientId, data }) => ({
        url: `/patients/${patientId}`,
        method: "PATCH",
        body: data,
      }),
      transformResponse: (response: { data: PatientDTO }): Patient =>
        mapPatientDTO(response.data),
      invalidatesTags: (result, error, { patientId }) => [
        { type: "Patients", id: patientId },
        { type: "Patients", id: "LIST" },
      ],
    }),

    deletePatient: builder.mutation<void, string>({
      query: (patientId) => ({
        url: `/patients/${patientId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, patientId) => [
        { type: "Patients", id: patientId },
        { type: "Patients", id: "LIST" },
      ],
    }),

    // â”€â”€ ACCESSIBILITY & SENSORY â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    createAccessibilitySensory: builder.mutation<
      AccessibilitySensoryDTO,
      { assessmentId: string; data: SensoryFormData; skipped?: boolean }
    >({
      query: ({ assessmentId, data, skipped }) => ({
        url: `/assessments/${assessmentId}/accessibility-sensory`,
        method: "POST",
        body: skipped
          ? mapSkippedSensoryStepToAccessibilitySensoryDTO()
          : mapSensoryFormDataToAccessibilitySensoryDTO(data),
      }),
      transformResponse: (
        response: AccessibilitySensoryResponseDTO,
      ): AccessibilitySensoryDTO => response.data,
      invalidatesTags: (result, error, { assessmentId }) => [
        { type: "Assessments" as const, id: assessmentId },
        { type: "Assessments" as const, id: "LIST" },
      ],
    }),

    upsertAccessibilitySensory: builder.mutation<
      AccessibilitySensoryDTO,
      { assessmentId: string; data: SensoryFormData; skipped?: boolean }
    >({
      query: ({ assessmentId, data, skipped }) => ({
        url: `/assessments/${assessmentId}/accessibility-sensory`,
        method: "PUT",
        body: skipped
          ? mapSkippedSensoryStepToAccessibilitySensoryDTO()
          : mapSensoryFormDataToAccessibilitySensoryDTO(data),
      }),
      transformResponse: (
        response: AccessibilitySensoryResponseDTO,
      ): AccessibilitySensoryDTO => response.data,
      invalidatesTags: (result, error, { assessmentId }) => [
        { type: "Assessments" as const, id: assessmentId },
        { type: "Assessments" as const, id: "LIST" },
      ],
    }),

    deleteAccessibilitySensory: builder.mutation<void, string>({
      query: (assessmentId) => ({
        url: `/assessments/${assessmentId}/accessibility-sensory`,
        method: "DELETE",
      }),
    }),

    // â”€â”€ CAREGIVER QUESTIONNAIRE â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    createCaregiverQuestionnaire: builder.mutation<
      CaregiverQuestionnaireDTO,
      {
        assessmentId: string;
        data: {
          neurological?: NeurologicalHistoryFormData;
          behavioral?: BehavioralFormData;
          cognitiveFunctional?: CognitiveFunctionalFormData;
        };
      }
    >({
      query: ({ assessmentId, data }) => {
        return {
          url: `/assessments/${assessmentId}/caregiver-questionnaire`,
          method: "POST",
          body: mapCaregiverQuestionnaireRequestBody(
            data.neurological ?? initialNeurologicalFormData,
            data.behavioral ?? initialBehavioralFormData,
            data.cognitiveFunctional ?? initialCognitiveFunctionalFormData,
            {
              neurological: !data.neurological,
              behavioral: !data.behavioral,
              cognitiveFunctional: !data.cognitiveFunctional,
            },
          ),
        };
      },
      transformResponse: (
        response: CaregiverQuestionnaireResponseDTO,
      ): CaregiverQuestionnaireDTO => response.data,
    }),

    upsertCaregiverQuestionnaire: builder.mutation<
      CaregiverQuestionnaireDTO,
      {
        assessmentId: string;
        data: {
          neurological?: NeurologicalHistoryFormData;
          behavioral?: BehavioralFormData;
          cognitiveFunctional?: CognitiveFunctionalFormData;
        };
      }
    >({
      query: ({ assessmentId, data }) => {
        return {
          url: `/assessments/${assessmentId}/caregiver-questionnaire`,
          method: "PUT",
          body: mapCaregiverQuestionnaireRequestBody(
            data.neurological ?? initialNeurologicalFormData,
            data.behavioral ?? initialBehavioralFormData,
            data.cognitiveFunctional ?? initialCognitiveFunctionalFormData,
            {
              neurological: !data.neurological,
              behavioral: !data.behavioral,
              cognitiveFunctional: !data.cognitiveFunctional,
            },
          ),
        };
      },
      transformResponse: (
        response: CaregiverQuestionnaireResponseDTO,
      ): CaregiverQuestionnaireDTO => response.data,
    }),

    deleteCaregiverQuestionnaire: builder.mutation<void, string>({
      query: (assessmentId) => ({
        url: `/assessments/${assessmentId}/caregiver-questionnaire`,
        method: "DELETE",
      }),
    }),

    // â”€â”€ PATIENT ASSESSMENTS â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    getPatientAssessments: builder.query<Assessment[], string>({
      query: (patientId) => ({
        url: `/patients/${patientId}/assessments`,
        method: "GET",
      }),
      transformResponse: (response: AssessmentsListResponseDTO): Assessment[] =>
        mapAssessmentsListResponseDTO(response.data.items),
      providesTags: (result, error, patientId) => [
        { type: "Patients", id: patientId },
        { type: "Assessments", id: "LIST" },
      ],
    }),

    createCurrentAssessment: builder.mutation<
      AssessmentDetailDTO,
      { patientId: string; data: CreateCurrentAssessmentPayload }
    >({
      query: ({ patientId, data }) => ({
        url: `/patients/${patientId}/assessments/current`,
        method: "POST",
        body: data,
      }),
      transformResponse: (response: { data: AssessmentDetailDTO }) =>
        response.data,
      invalidatesTags: (result, error, { patientId }) => [
        { type: "Patients", id: patientId },
        { type: "Assessments", id: "LIST" },
        ...(result?.id
          ? [{ type: "Assessments" as const, id: result.id }]
          : []),
      ],
    }),
    checkPatientExists: builder.query<
      boolean,
      { phone: string; exclude_patient_id?: string }
    >({
      query: ({ phone, exclude_patient_id }) => {
        const qs = new URLSearchParams();

        qs.append("phone", phone);

        if (exclude_patient_id) {
          qs.append("exclude_patient_id", exclude_patient_id);
        }

        return {
          url: `/patients/exist?${qs.toString()}`,
          method: "GET",
        };
      },

      transformResponse: (response: { data: { exists: boolean } }): boolean =>
        response.data.exists,
    }),

    getPatientAssessmentDetail: builder.query<
      PatientAssessmentDetailDTO,
      { patientId: string; assessmentId: string }
    >({
      query: ({ patientId, assessmentId }) => ({
        url: `/patients/${patientId}/assessments/${assessmentId}`,
        method: "GET",
      }),
      providesTags: (result, error, { assessmentId }) => [
        { type: "Assessments", id: assessmentId },
        { type: "Assessments", id: "LIST" },
      ],
    }),

    getScreeningReportHtml: builder.query<
      { assessment_id: string; html: string },
      { patientId: string; assessmentId: string }
    >({
      query: ({ patientId, assessmentId }) => ({
        url: `/patients/${patientId}/assessments/${assessmentId}/screening-report/html`,
        method: "GET",
        headers: {
          "X-Client-Timezone":
            Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        },
      }),
      transformResponse: (response: {
        data: { assessment_id: string; html: string };
      }) => response.data,
      providesTags: (result, error, { assessmentId }) => [
        { type: "Assessments", id: `report-${assessmentId}` },
      ],
    }),

    retryScreeningReportGeneration: builder.mutation<
      AssessmentDetailDTO,
      { patientId: string; assessmentId: string }
    >({
      query: ({ patientId, assessmentId }) => ({
        url: `/patients/${patientId}/assessments/${assessmentId}/screening-report/retry`,
        method: "POST",
      }),
      transformResponse: (response: { data: AssessmentDetailDTO }) =>
        response.data,
      invalidatesTags: (result, error, { patientId, assessmentId }) => [
        { type: "Patients", id: patientId },
        { type: "Assessments", id: assessmentId },
        { type: "Assessments", id: "LIST" },
        { type: "Assessments", id: `report-${assessmentId}` },
      ],
    }),
  }),
});

export const {
  useGetPatientsQuery,
  useGetPatientByIdQuery,
  useCreatePatientMutation,
  useUpdatePatientMutation,
  useDeletePatientMutation,

  // Accessibility & Sensory
  useCreateAccessibilitySensoryMutation,
  useUpsertAccessibilitySensoryMutation,
  useDeleteAccessibilitySensoryMutation,

  // Caregiver Questionnaire
  useCreateCaregiverQuestionnaireMutation,
  useUpsertCaregiverQuestionnaireMutation,
  useDeleteCaregiverQuestionnaireMutation,

  // Assessments
  useGetPatientAssessmentsQuery,
  useCreateCurrentAssessmentMutation,
  useGetPatientAssessmentDetailQuery,
  useGetScreeningReportHtmlQuery,
  useRetryScreeningReportGenerationMutation,

  useLazyCheckPatientExistsQuery,
} = patientsApi;
